<template>
    <OrthographicWrapper :visible="visible">
        <TextRows
            ref="refTextRows"
            :highlightIndex="-1"
            :highlightFg="false"
            color="#CC8418"
            :text="bgCategoryText"
        />
    </OrthographicWrapper>

    <template v-if="loaded">
        <component
            ref="refObjects"
            v-for="data in objects"
            :key="data.id"
            :is="data.component"
            :object="data.object"
        />
    </template>

    <TextWrap ref="refTextWrap" :text="fgCategoryText" :scale="0.4" />
</template>

<script setup>
    import { formatCategoryText } from '#imports';
    import { computed, ref, shallowRef } from 'vue';

    import { useThreeObject } from '@resn/gozer-vue';
    import gsap from '@resn/gsap';

    import GrailBaseball from './GrailBaseball.vue';
    import GrailBasketball from './GrailBasketball.vue';
    import GrailFootball from './GrailFootball.vue';
    import GrailPokeball from './GrailPokeball.vue';
    import TextRows from '~/components/gl/Cards/reveals/chase/modules/TextRows.vue';
    import TextWrap from '~/components/gl/Cards/reveals/chase/modules/TextWrap.vue';
    import OrthographicWrapper from '~/components/gl/common/OrthographicWrapper';
    import { useGlobalAssets } from '~/providers/GlobalAssets';
    import { logger } from '~/utils/logger';

    const props = defineProps({
        category: { type: String, default: null },
    });

    const refTextRows = ref(null);
    const refTextWrap = ref(null);
    const refObjects = ref([]);
    const categoryId = computed(() =>
        typeof props.category === 'string' ? props.category.toLowerCase() : ''
    );
    const refObject = computed(() => refObjects.value.find((o) => o.id === categoryId.value));

    const visible = ref(true);
    const loaded = ref(false);
    const objects = shallowRef([]);

    const componentTypes = [
        { id: 'basketball', name: 'basketball', component: GrailBasketball },
        { id: 'baseball', name: 'baseball', component: GrailBaseball },
        { id: 'football', name: 'football', component: GrailFootball },
        { id: 'pokemon', name: 'pokeball', component: GrailPokeball },
        // { id: 'multisport', name: 'Scene', component: MultiSport },
    ];

    const { object } = useThreeObject(null, { name: 'GrailCategory', props: { v: visible } });

    const bgCategoryText = computed(() => categoryId.value);
    const fgCategoryText = computed(() => formatCategoryText(categoryId.value));

    const onLoaded = ({ data }) => {
        const { packSceneObjects } = data;

        const _objects = [];
        const scene = packSceneObjects.scene.clone(true);
        scene.traverse((child) => {
            const component = componentTypes.find((c) => c.name === child.name);

            if (component) {
                _objects.push({
                    id: component.id,
                    object: child,
                    component: component.component,
                });
            }
        });

        object.add(scene);
        objects.value = _objects;

        requestAnimationFrame(() => {
            loaded.value = true;
        });
    };

    useGlobalAssets(onLoaded);

    const show = ({ delay = 0 } = {}) => {
        const tl = new gsap.timeline({
            delay,
            onStart: () => {
                visible.value = true;
                logger.debug('Grail category animation start');

                emit('start');
            },
            onComplete: () => {
                visible.value = false;
                logger.debug('Grail category animation complete');
            },
        });

        const objectStart = 0.6;
        const objectEnd = 2.5;
        const textStart = objectStart + 0.6;
        const timeScale = 0.5;
        const ambientDist = 0.06;

        refObject.value?.show({ inDelay: objectStart, outDelay: objectEnd });
        tl.add(() => refTextRows.value?.show({ timeScale }), textStart);
        tl.add(() => refTextWrap.value?.show({ timeScale, ambientDist }), textStart + 0.2);
        tl.add(() => emit('complete'), textStart + 6);

        return tl;
    };

    const emit = defineEmits(['start', 'complete']);

    defineExpose({ show });
</script>
